<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAllSegments"></b-form-checkbox>
        <span @click="checkedAllSegments = !checkedAllSegments">Strava Segments</span>
        <div style="float: right;">
          <img @click="expanded = !expanded" style="height: 13px; cursor: pointer;" :src="expanded ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expanded">
        <b-form-checkbox class="display-inline marginright5" v-model="active"></b-form-checkbox>
        <span @click="active = !active">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactive"></b-form-checkbox>
        <span @click="inactive = !inactive">Inactive</span>
      </div>
    </div>  
  </div>
</template>

<script lang="ts">
import BaseComponent from './Base.vue';
import { EventBus } from '@/ts/EventBus';
import { AlertStatus, DownloadStatus } from '@/ts/types';
import { defineComponent } from "vue";

export default defineComponent({
    extends: BaseComponent,
    data() {
        const checkedAllSegments: boolean = false;

        return {
            checkedAllSegments,
            loading: false,
            expanded: false,
            active: true,
            inactive: false
        };
    },
    created() {
      EventBus.$on('downloadExternalData', () => this.downloadSegmentsStrava());
      EventBus.$on('toggleStravaSegments', () => {
        if (this.$options.result) {
          this.checkedAllSegments = !this.checkedAllSegments;
        }
      });
      EventBus.$on('selectedAreaChanged', (newArea: string, oldArea: string) => this.onSelectedAreaChanged(newArea, oldArea));
    },
    methods: {

      isPointInSelectedArea(lat: number | string, lon: number | string) {
        // @ts-ignore
        const areaKey = window.selectedArea;
        if (!areaKey) return true;
        // @ts-ignore
        const area = window.areas[areaKey];
        if (!area || !area[0] || !area[1]) return true;
        const minLat = Math.min(area[0][0], area[1][0]);
        const maxLat = Math.max(area[0][0], area[1][0]);
        const minLon = Math.min(area[0][1], area[1][1]);
        const maxLon = Math.max(area[0][1], area[1][1]);
        const plat = Number(lat);
        const plon = Number(lon);
        if (Number.isNaN(plat) || Number.isNaN(plon)) return false;
        return (plat >= minLat && plat <= maxLat && plon >= minLon && plon <= maxLon);
      },

      isPathInSelectedArea(path: any[]) {
        if (!path || !path.length) return false;
        for (const p of path) {
          if (Array.isArray(p) && p.length >= 2) {
            if (this.isPointInSelectedArea(p[0], p[1])) return true;
          } else if (p && typeof p === 'object') {
            const lat = p.lat ?? p.latitude ?? p[0];
            const lon = p.lon ?? p.lng ?? p.longitude ?? p[1];
            if (lat !== undefined && lon !== undefined && this.isPointInSelectedArea(lat, lon)) return true;
          }
        }
        return false;
      },

      onSelectedAreaChanged(newArea: string, oldArea: string) {
        if (this.$options.cycleways) {
          for (const segment of this.$options.cycleways) {
            try { segment.removeFrom(window.mainmap); } catch (e) {}
          }
        }
        if (this.$options.result) {
          this.processSegmentsStrava();
        }
      },
        downloadSegmentsStrava() {
            this.loading = true;
            const endPoint = window.apiHost + 'api/stringfield/stravasegments/';
            this.downloadUrlWithCache(endPoint, 'stravasegments', 'stravasegments.txt', null, true).then((response) => {
              this.$options.result = response[1].results;
              if (response[0] == DownloadStatus.cache_fresh) {
                this.createAlert(AlertStatus.success, this.$t('toursLoaded', [response[1].results.length]).toString(), 2000);
              }
              if (response[0] == DownloadStatus.api_fresh) {
                this.createAlert(AlertStatus.success, this.$t('toursDownloaded', [response[1].results.length]).toString(), 2000);
              }
              if (response[0] == DownloadStatus.cache_last) {
                this.createAlert(AlertStatus.warning, this.$t('toursLoaded', [response[1].results.length]).toString(), 2000);
              }
            }).catch((error) => {
              this.createAlert(AlertStatus.danger, this.$t('toursError').toString(), 2000);
            }).finally(() => {
              this.loading = false;
            });
        },
        processSegmentsStrava() {
            const tours: any[] = [];
            for (const responseTour of this.$options.result) {
              // only include segments that have at least one point inside selected area
              if (!this.isPathInSelectedArea(responseTour['points'])) {
                continue;
              }
              const cycleway = new L.Polyline(responseTour['points'], {
                color: 'red',
                weight: 3,
                opacity: 1,
                smoothFactor: 1,
              });
              cycleway.on('mouseover', () => {
                cycleway.setStyle({'color': 'yellow'});
                cycleway.bringToFront();
              });
              cycleway.on('mouseout', () => {
                cycleway.setStyle({'color': 'red'});
              });
              if (window.window.isDesktop) {
                cycleway.on('click', () => {
                  window.open('https://www.strava.com/segments/' + responseTour['id'], '_blank').focus();
                })
              }
              cycleway.is_active = responseTour.is_active;
              tours.push(cycleway);
            }
            this.$options.cycleways = tours;
            this.onCheckedAllSegmentsChanged();
        },
        onCheckedAllSegmentsChanged() {
            if (this.$options.cycleways) {
              for(const segment of this.$options.cycleways) {
                if (this.checkedAllSegments) {
                  if (segment.is_active) {
                    if (this.active) {
                      segment.addTo(window.mainmap);
                    } else {
                      segment.removeFrom(window.mainmap);
                    }
                  } else {
                    if (this.inactive) {
                      segment.addTo(window.mainmap);
                    } else {
                      segment.removeFrom(window.mainmap);
                    }
                  }
                } else {
                  segment.removeFrom(window.mainmap);
                }
              }
            } else {
              this.processSegmentsStrava();
            }
        }
    },
    watch: {
        "checkedAllSegments": [{
            handler: "onCheckedAllSegmentsChanged"
        }],
        "active": [{
            handler: "onCheckedAllSegmentsChanged"
        }],
        "inactive": [{
            handler: "onCheckedAllSegmentsChanged"
        }]
    }
})


</script>